import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
    DashboardTitle,
    DashboardWrapper,
} from '../../components';
import DashboardInfo from "../../models/DashboardInfo";
import { isScreenLinked } from "../../models/Screen";
import DashboardService from "../../services/use_case/dashboard/DashboardService";
import DashboardResponse from "../../services/use_case/dashboard/DashboardResponse";
import { AxiosResponse } from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsers, faChevronRight, faLayerGroup,
} from '@fortawesome/free-solid-svg-icons';

import { ReactComponent as DashboardIcon } from '../../assets/icons-menu/dashboard.svg';
import { ReactComponent as DisplayIcon } from '../../assets/icons-menu/display.svg';
import { ReactComponent as DisplayGroupIcon } from '../../assets/icons-menu/display-group.svg';
import { ReactComponent as InksIcon } from '../../assets/icons-menu/inks.svg';
import { ReactComponent as ScheduleIcon } from '../../assets/icons-menu/schedule.svg';
import { ReactComponent as ListsIcon } from '../../assets/icons-menu/lists.svg';
import { ReactComponent as MediaIcon } from '../../assets/icons-menu/media.svg';
import { ReactComponent as WidgetsIcon } from '../../assets/icons-menu/widgets.svg';
import { ReactComponent as TagIcon } from '../../assets/icons-menu/tag.svg';
import { ReactComponent as LicencesIcon } from '../../assets/icons-menu/licences.svg';
import { ReactComponent as UsersIcon } from '../../assets/icons-menu/users.svg';
import { ReactComponent as AdministrationIcon } from '../../assets/icons-menu/administration.svg';

interface DashboardProps {}

// ── Animations ────────────────────────────────────────────────────────────────

const shimmer = keyframes`
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
`;

// ── Shared type ───────────────────────────────────────────────────────────────

type ColorVariant = 'accent' | 'info' | 'success' | 'offline';

// ── Stat Cards ────────────────────────────────────────────────────────────────

const StatCard = styled.div`
    background-color: var(--cds-surface);
    border: 1px solid var(--cds-border);
    border-radius: 8px;
    padding: 20px 24px;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const StatCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const StatLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--cds-text-muted);
`;

const StatIconWrap = styled.div<{ $variant: 'accent' | 'info' }>`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${({ $variant }) =>
        $variant === 'accent'
            ? 'var(--cds-accent-alpha-08)'
            : 'rgba(var(--cds-info-rgb), 0.08)'};
    color: ${({ $variant }) =>
        $variant === 'accent' ? 'var(--cds-accent)' : 'var(--cds-info)'};
    font-size: 14px;
    flex-shrink: 0;

    svg {
        width: 14px;
        height: 14px;
    }

    svg * {
        fill: currentColor !important;
        stroke: currentColor !important;
    }
`;

const StatValue = styled.div`
    font-size: 2.2rem;
    font-weight: 700;
    color: var(--cds-text-primary);
    line-height: 1;
`;

const StatBadgesRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: auto;
`;

const StatBadge = styled.span<{ $variant?: ColorVariant }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 500;
    padding: 3px 8px;
    border-radius: 20px;
    color: ${({ $variant }) => {
        switch ($variant) {
            case 'accent':  return 'var(--cds-accent)';
            case 'success': return 'var(--cds-success)';
            case 'info':    return 'var(--cds-info)';
            default:        return 'var(--cds-text-muted)';
        }
    }};
    background: ${({ $variant }) => {
        switch ($variant) {
            case 'accent':  return 'var(--cds-accent-alpha-08)';
            case 'success': return 'var(--cds-success-alpha-10)';
            case 'info':    return 'rgba(var(--cds-info-rgb), 0.08)';
            default:        return 'var(--cds-neutral-alpha-04)';
        }
    }};
    border: 1px solid ${({ $variant }) => {
        switch ($variant) {
            case 'accent':  return 'rgba(var(--cds-accent-rgb), 0.2)';
            case 'success': return 'rgba(var(--cds-success-rgb), 0.2)';
            case 'info':    return 'rgba(var(--cds-info-rgb), 0.2)';
            default:        return 'var(--cds-neutral-alpha-06)';
        }
    }};
`;

const StatDot = styled.span<{ $variant: ColorVariant }>`
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${({ $variant }) => {
        switch ($variant) {
            case 'accent':  return 'var(--cds-accent)';
            case 'success': return 'var(--cds-success)';
            case 'info':    return 'var(--cds-info)';
            default:        return 'var(--cds-text-muted)';
        }
    }};
    display: inline-block;
    flex-shrink: 0;
`;

// ── Status Donut ──────────────────────────────────────────────────────────────

const SectionCard = styled.div`
    background-color: var(--cds-surface);
    border: 1px solid var(--cds-border);
    border-radius: 8px;
    padding: 20px 24px;
`;

const SectionLabelSmall = styled.div`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--cds-text-muted);
    margin-bottom: 16px;
`;

const DonutWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
`;

const DonutSvgWrap = styled.div`
    width: 110px;
    height: 110px;
    flex-shrink: 0;
`;

const AnimatedCircle = styled.circle`
    transition: stroke-dasharray 0.65s cubic-bezier(0.4, 0, 0.2, 1),
                stroke-dashoffset 0.65s cubic-bezier(0.4, 0, 0.2, 1);
`;

const DonutLegend = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
`;

const LegendDot = styled.div<{ $variant: ColorVariant }>`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ $variant }) => {
        switch ($variant) {
            case 'success': return 'var(--cds-success)';
            case 'accent':  return 'var(--cds-accent)';
            default:        return 'var(--cds-text-muted)';
        }
    }};
    flex-shrink: 0;
`;

const LegendText = styled.span`
    font-size: 12px;
    color: var(--cds-text-secondary);
    flex: 1;
`;

const LegendCount = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: var(--cds-text-primary);
`;

// ── Section header ────────────────────────────────────────────────────────────

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    margin-top: 4px;
`;

const SectionTitle = styled.div`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--cds-text-muted);
`;

// ── Quick Action Cards ────────────────────────────────────────────────────────

const ActionCard = styled(Link)`
    background-color: var(--cds-surface);
    border: 1px solid var(--cds-border);
    border-radius: 8px;
    padding: 18px 20px;
    display: flex;
    align-items: center;
    gap: 14px;
    text-decoration: none;
    color: var(--cds-text-primary);
    height: 100%;
    transition: border-color 0.15s, background-color 0.15s;

    &:hover {
        border-color: var(--cds-accent);
        background-color: var(--cds-accent-alpha-06);
        color: var(--cds-text-primary);
        text-decoration: none;
    }
`;

const ActionIconWrap = styled.div`
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(var(--cds-accent-rgb), 0.1), rgba(var(--cds-pink-rgb), 0.1));
    border: 1px solid rgba(var(--cds-accent-rgb), 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--cds-accent);
    font-size: 14px;
    flex-shrink: 0;

    svg {
        width: 16px;
        height: 16px;
    }

    svg * {
        fill: currentColor !important;
        stroke: currentColor !important;
    }
`;

const ActionLabel = styled.span`
    font-size: 14px;
    font-weight: 500;
    flex: 1;
`;

const ActionArrow = styled.div`
    font-size: 11px;
    color: var(--cds-border);
    transition: color 0.15s, transform 0.15s;

    ${ActionCard}:hover & {
        color: var(--cds-accent);
        transform: translateX(3px);
    }
`;

// ── Skeleton ──────────────────────────────────────────────────────────────────

const SkeletonBox = styled.div<{ $h?: string; $w?: string; $r?: string }>`
    background: linear-gradient(90deg, var(--cds-surface) 25%, var(--cds-surface-raised) 50%, var(--cds-surface) 75%);
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s infinite;
    height: ${({ $h }) => $h || '16px'};
    width: ${({ $w }) => $w || '100%'};
    border-radius: ${({ $r }) => $r || '4px'};
`;

const SkeletonCard = styled.div`
    background-color: var(--cds-surface);
    border: 1px solid var(--cds-border);
    border-radius: 8px;
    padding: 20px 24px;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

// ── Component ─────────────────────────────────────────────────────────────────

const Dashboard: React.FC<DashboardProps> = () => {

    const [t] = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [info, setInfo] = useState<DashboardInfo | null>(null);

    const dashboardService = new DashboardService();

    useEffect(() => {
        _fetchData();
        return () => { dashboardService.cancelRequest(); };
    }, []);

    const _fetchData = () => {
        setIsLoading(true);
        dashboardService.fetch()
            .then((response: AxiosResponse<DashboardResponse>) => {
                setInfo(response.data.data);
                setIsLoading(false);
            })
            .catch(() => { setIsLoading(false); });
    };

    const _getStatusCounts = () => {
        if (!info) return { configured: 0, unpaired: 0 };
        const configured = info.screens.filter(s =>  isScreenLinked(s)).length;
        const unpaired   = info.screens.filter(s => !isScreenLinked(s)).length;
        return { configured, unpaired };
    };

// ── Stats ──────────────────────────────────────────────────────────────────

    const _renderStats = () => {

        if (isLoading || !info) {
            return (
                <div className="row mt-2">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="col-6 col-md-3 mt-2">
                            <SkeletonCard>
                                <div className="d-flex justify-content-between">
                                    <SkeletonBox $h="11px" $w="70px" />
                                    <SkeletonBox $h="32px" $w="32px" $r="8px" />
                                </div>
                                <SkeletonBox $h="38px" $w="55px" />
                                <SkeletonBox $h="22px" $w="95px" $r="20px" />
                            </SkeletonCard>
                        </div>
                    ))}
                </div>
            );
        }

        const { configured, unpaired } = _getStatusCounts();

        return (
            <div className="row mt-2">

                {/* Total Screens */}
                <div className="col-6 col-md-3 mt-2">
                    <StatCard>
                        <StatCardHeader>
                            <StatLabel>{t('Screens')}</StatLabel>
                            <StatIconWrap $variant="accent">
                                <DisplayIcon />
                            </StatIconWrap>
                        </StatCardHeader>
                        <StatValue>{info.total_screens}</StatValue>
                        <StatBadgesRow>
                            <StatBadge $variant="success">
                                <StatDot $variant="success" />
                                {configured} {t('active')}
                            </StatBadge>
                            {unpaired > 0 && (
                                <StatBadge>{unpaired} {t('Unpaired')}</StatBadge>
                            )}
                        </StatBadgesRow>
                    </StatCard>
                </div>

                {/* Configured screens */}
                <div className="col-6 col-md-3 mt-2">
                    <StatCard>
                        <StatCardHeader>
                            <StatLabel>{t('Configured')}</StatLabel>
                            <StatIconWrap $variant="accent">
                                <FontAwesomeIcon icon={faLayerGroup} />
                            </StatIconWrap>
                        </StatCardHeader>
                        <StatValue>{configured}</StatValue>
                        <StatBadgesRow>
                            <StatBadge $variant="accent">
                                {info.total_screens > 0
                                    ? Math.round((configured / info.total_screens) * 100)
                                    : 0}% {t('of_total')}
                            </StatBadge>
                        </StatBadgesRow>
                    </StatCard>
                </div>

                {/* Active Users */}
                {/* <div className="col-6 col-md-3 mt-2">
                    <StatCard>
                        <StatCardHeader>
                            <StatLabel>{t('Users')}</StatLabel>
                            <StatIconWrap $variant="accent">
                                <FontAwesomeIcon icon={faUsers} />
                            </StatIconWrap>
                        </StatCardHeader>
                        <StatValue>{info.total_active_users}</StatValue>
                        <StatBadgesRow>
                            <StatBadge $variant="accent">{t('Active users')}</StatBadge>
                            {info.total_inactive_users > 0 && (
                                <StatBadge>{info.total_inactive_users} {t('inactive')}</StatBadge>
                            )}
                        </StatBadgesRow>
                    </StatCard>
                </div> */}

                {/* Schedules */}
                <div className="col-6 col-md-3 mt-2">
                    <StatCard>
                        <StatCardHeader>
                            <StatLabel>{t('Schedule')}</StatLabel>
                            <StatIconWrap $variant="accent">
                                <ScheduleIcon />
                            </StatIconWrap>
                        </StatCardHeader>
                        <StatValue>{info.total_schedules}</StatValue>
                        <StatBadgesRow>
                            <StatBadge $variant="accent">{t('Active schedule')}</StatBadge>
                        </StatBadgesRow>
                    </StatCard>
                </div>

            </div>
        );
    };

    // ── Status donut ───────────────────────────────────────────────────────────

    const _renderStatusBar = () => {
        if (!info || info.total_screens === 0) return null;

        const { configured, unpaired } = _getStatusCounts();
        const total = info.total_screens;

        // SVG donut math
        const R  = 36;
        const SW = 10;
        const C  = 2 * Math.PI * R; // ≈ 226.2

        // Gap (px along circumference) between adjacent segments
        const activeCount = [configured, unpaired].filter(v => v > 0).length;
        const GAP = activeCount > 1 ? 3 : 0;

        // Slot = proportional arc length; drawn = slot minus gap
        const confSlot   = (configured / total) * C;
        const offSlot    = (unpaired / total) * C;

        const confDrawn = configured > 0 ? Math.max(0, confSlot - GAP) : 0;
        const offDrawn  = unpaired > 0   ? Math.max(0, offSlot  - GAP) : 0;

        // Cumulative offsets (negative = advance CW after rotate(-90))
        const offOffset = confSlot;

        const segments = [
            { drawn: confDrawn, offset: 0,         color: 'var(--cds-accent)',     show: configured > 0 },
            { drawn: offDrawn,  offset: offOffset,  color: 'var(--cds-text-muted)', show: unpaired > 0 },
        ];

        return (
            <div className="row mt-2">
            <div className="col-6 col-md-3 mt-2">
                <SectionCard>
                    <SectionLabelSmall>{t('Screen_status')}</SectionLabelSmall>
                    <DonutWrapper>

                        {/* ── SVG donut ── */}
                        <DonutSvgWrap>
                            <svg viewBox="0 0 100 100" style={{ width: '100%', height: '100%' }}>

                                {/* Background track */}
                                <circle
                                    cx="50" cy="50" r={R}
                                    fill="none"
                                    stroke="var(--cds-surface-raised)"
                                    strokeWidth={SW}
                                />

                                {/* Colored segments */}
                                {segments.map((seg, i) => seg.show && (
                                    <AnimatedCircle
                                        key={i}
                                        cx="50" cy="50" r={R}
                                        fill="none"
                                        stroke={seg.color}
                                        strokeWidth={SW}
                                        strokeDasharray={`${seg.drawn} ${C}`}
                                        strokeDashoffset={-seg.offset}
                                        transform="rotate(-90 50 50)"
                                        strokeLinecap="butt"
                                    />
                                ))}

                                {/* Center label */}
                                <text
                                    x="50" y="46"
                                    textAnchor="middle"
                                    fill="var(--cds-text-primary)"
                                    fontSize="18"
                                    fontWeight="700"
                                    fontFamily="Nunito Sans, sans-serif"
                                >
                                    {total}
                                </text>
                                <text
                                    x="50" y="59"
                                    textAnchor="middle"
                                    fill="var(--cds-text-muted)"
                                    fontSize="9"
                                    fontFamily="Nunito Sans, sans-serif"
                                >
                                    {t('Screens').toLowerCase()}
                                </text>

                            </svg>
                        </DonutSvgWrap>

                        {/* ── Legend ── */}
                        <DonutLegend>
                            <LegendItem>
                                <LegendDot $variant="accent" />
                                <LegendText>{t('Configured')}</LegendText>
                                <LegendCount>{configured}</LegendCount>
                            </LegendItem>
                            <LegendItem>
                                <LegendDot $variant="offline" />
                                <LegendText>{t('Unpaired')}</LegendText>
                                <LegendCount>{unpaired}</LegendCount>
                            </LegendItem>
                        </DonutLegend>

                    </DonutWrapper>
                </SectionCard>
            </div>
            </div>
        );
    };

    // ── Quick actions ──────────────────────────────────────────────────────────

    const _renderQuickActions = () => {
        const actions = [
            { to: '/screens',   Icon: DisplayIcon,   label: t('Screens')   },
            { to: '/playlists', Icon: ListsIcon,      label: t('Playlists') },
            { to: '/library',   Icon: MediaIcon,      label: t('Library')   },
            { to: '/schedule',  Icon: ScheduleIcon,   label: t('Schedule')  },
        ];

        return (
            <div className="mt-3">
                <SectionHeader>
                    <SectionTitle>{t('Quick_actions')}</SectionTitle>
                </SectionHeader>
                <div className="row">
                    {actions.map(action => (
                        <div key={action.to} className="col-6 col-md-3 mb-2">
                            <ActionCard to={action.to}>
                                <ActionIconWrap>
                                    <action.Icon />
                                </ActionIconWrap>
                                <ActionLabel>{action.label}</ActionLabel>
                                <ActionArrow>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </ActionArrow>
                            </ActionCard>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <DashboardWrapper>

            <DashboardTitle
                title={t('Dashboard')}
                isLoading={isLoading}
                withRefreshIcon={!isLoading}
                refreshIconCallBack={_fetchData}
            />

            {_renderStats()}
            {_renderStatusBar()}
            {_renderQuickActions()}

            <br />

        </DashboardWrapper>
    );

};

export default React.memo(Dashboard);
